/**
 * Export EQ presets in Equalizer APO parametric EQ format.
 *
 * Format specification:
 *   Preamp: <value> dB
 *   Filter <n>: ON <type> Fc <freq> Hz Gain <gain> dB Q <q>
 *
 * Where type is one of: PK, LS, HS, LP, HP, NO
 * This format is accepted by Music Assistant, Equalizer APO,
 * and Room EQ Wizard.
 */

import type { EqPreset } from "../types";

/**
 * Filter type display names for APO format.
 *
 * Equalizer APO uses two-letter codes for standard parametric shelves:
 *   LS = Low Shelf (with Fc, Gain, Q)
 *   HS = High Shelf (with Fc, Gain, Q)
 * LSC/HSC are slope-based variants that require a dB-slope parameter,
 * which we do not emit, so we map our internal LSC/HSC to LS/HS.
 */
const TYPE_MAP: Record<string, string> = {
  PK: "PK",
  LSC: "LS",
  HSC: "HS",
  LP: "LP",
  HP: "HP",
  NO: "NO",
};

/**
 * Format a frequency value for display.
 * Uses integer Hz for values >= 100 Hz, one decimal for smaller values.
 */
function formatFreq(freq: number): string {
  if (freq >= 100) {
    return Math.round(freq).toString();
  }
  return freq.toFixed(1);
}

/**
 * Format a gain value with consistent decimal places.
 */
function formatGain(gain: number): string {
  return gain.toFixed(1);
}

/**
 * Format a Q value with two decimal places.
 */
function formatQ(q: number): string {
  return q.toFixed(2);
}

/**
 * Serialize an EQ preset to Equalizer APO format string.
 */
export function serializeApoPreset(preset: EqPreset): string {
  const lines: string[] = [];

  // Preamp line
  lines.push(`Preamp: ${formatGain(preset.preamp)} dB`);

  // Filter lines
  for (let i = 0; i < preset.bands.length; i++) {
    const band = preset.bands[i];
    const state = band.enabled ? "ON" : "OFF";
    const type = TYPE_MAP[band.type] || "PK";
    const freq = formatFreq(band.frequency);
    const gain = formatGain(band.gain);
    const q = formatQ(band.q);

    lines.push(
      `Filter ${i + 1}: ${state} ${type} Fc ${freq} Hz Gain ${gain} dB Q ${q}`
    );
  }

  return lines.join("\n") + "\n";
}

/**
 * Generate a filename for the APO preset with a timestamp.
 */
export function generateFilename(): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `sound-meter-eq-${date}-${time}.txt`;
}

/**
 * Serialize raw measurement data and EQ fitting details for debugging.
 */
export interface DebugData {
  frequencies: number[];
  measured: Float64Array;
  target: Float64Array;
  preset: EqPreset;
  peakGain: number;
}

/**
 * Format a debug report text file.
 */
export function serializeDebugData(data: DebugData): string {
  const lines: string[] = [];
  const now = new Date().toISOString();

  lines.push(`# Sound Meter Debug Export`);
  lines.push(`# Generated: ${now}`);
  lines.push(`#`);
  lines.push(`# Preamp: ${data.preset.preamp.toFixed(2)} dB`);
  lines.push(`# Peak Gain: ${data.peakGain.toFixed(2)} dB`);
  lines.push(`# Bands: ${data.preset.bands.length}`);
  lines.push(`#`);
  lines.push(`# Frequency Response Data`);
  lines.push(`# Freq(Hz)\tMeasured(dB)\tTarget(dB)\tError(dB)`);

  for (let i = 0; i < data.frequencies.length; i++) {
    const f = data.frequencies[i];
    const m = Number.isFinite(data.measured[i]) ? data.measured[i].toFixed(2) : "-Inf";
    const t = Number.isFinite(data.target[i]) ? data.target[i].toFixed(2) : "-Inf";
    const e = Number.isFinite(data.target[i] - data.measured[i])
      ? (data.target[i] - data.measured[i]).toFixed(2)
      : "NaN";
    lines.push(`${f.toFixed(1)}\t${m}\t${t}\t${e}`);
  }

  lines.push(`#`);
  lines.push(`# Generated EQ Filters`);
  lines.push(`# Idx\tType\tFc(Hz)\tGain(dB)\tQ`);
  for (let i = 0; i < data.preset.bands.length; i++) {
    const b = data.preset.bands[i];
    lines.push(
      `${i + 1}\t${b.type}\t${b.frequency.toFixed(1)}\t${b.gain.toFixed(2)}\t${b.q.toFixed(2)}`
    );
  }

  return lines.join("\n") + "\n";
}

/**
 * Trigger a browser download of the APO preset file.
 */
export function downloadApoPreset(preset: EqPreset): void {
  const content = serializeApoPreset(preset);
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = generateFilename();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger a browser download of the debug data file.
 */
export function downloadDebugData(data: DebugData): void {
  const content = serializeDebugData(data);
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `sound-meter-debug-${generateFilename().replace("sound-meter-eq-", "")}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
